"use client";

import { motion } from "framer-motion";
import { Reveal } from "@/components/motion/reveal";

/**
 * Fleet section — a space scene: continuous comets and a centred black hole
 * (threats get absorbed, no one gets hurt) behind a liquid-glass fleet board.
 * Numbers are representative sample data, labelled as an illustrative
 * preview — not a live feed, since no hosted instance is running right now
 * (see the status section on this page). The fleet mix mirrors reality
 * (Windows-majority, one Linux box) with device names the way real orgs
 * actually label machines (Windows default DESKTOP-XXXXXXX plus role-based
 * names).
 *
 * Motion: comets + accretion-disc spin are transform-only CSS loops whose
 * restarts happen off-screen (nothing pops in or out); trust bars animate in
 * on scroll. All of it dies under prefers-reduced-motion via the global kill
 * rule.
 */

const ENDPOINTS = [
  { host: "DESKTOP-7K2MQ4P", os: "Windows 11 Pro · x64", trust: 92, state: "ok" },
  { host: "RECEPTION-PC02", os: "Windows 10 · x64", trust: 74, state: "watch" },
  { host: "TILL-WS01", os: "Windows 10 · x86", trust: 48, state: "restrict" },
  { host: "ACCT-UBUNTU01", os: "Ubuntu 24.04 · x64", trust: 88, state: "ok" },
];

const STATE_COLOR: Record<string, string> = {
  ok: "#3fb950",
  watch: "#e0b53a",
  restrict: "#e3902c",
};

export function LiveConsole() {
  return (
    <section className="relative mx-auto w-full max-w-6xl px-6 py-20">
      <Reveal>
        <div className="mb-10 max-w-2xl">
          <span className="mono-label text-accent-text">admin console preview</span>
          <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight sm:text-4xl">
            Your whole fleet, on one screen.
          </h2>
          <p className="mt-4 text-lg text-muted">
            Trust scores, active threats, and device health, all in one view —
            no refresh, no digging. Threats never reach your devices; they get
            pulled in and absorbed before they can do harm. Below is an
            illustrative mockup of the admin console, not a live feed.
          </p>
        </div>
      </Reveal>

      <Reveal>
        <div className="fleet-scene p-5 text-white sm:p-8">
          {/* Sky: black hole + continuous comets (behind the glass) */}
          <div aria-hidden className="bh">
            <span className="bh-disc" />
            <span className="bh-core" />
          </div>
          <span aria-hidden className="comet" style={{ top: "18%", "--dur": "17s", "--delay": "0s" } as React.CSSProperties} />
          <span aria-hidden className="comet" style={{ top: "52%", width: 130, "--dur": "23s", "--delay": "-9s" } as React.CSSProperties} />
          <span aria-hidden className="comet" style={{ top: "76%", width: 200, "--dur": "20s", "--delay": "-15s" } as React.CSSProperties} />
          <span aria-hidden className="comet" style={{ top: "36%", width: 110, "--dur": "27s", "--delay": "-4s" } as React.CSSProperties} />

          {/* Liquid-glass fleet board */}
          <div className="relative z-10">
            <div className="lg-panel flex items-center justify-between px-4 py-2.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.14em]">Fleet overview</span>
              <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide">
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: "#3fb950" }} />
                Sample data
              </span>
            </div>

            {/* Stat chips */}
            <div className="mt-3 grid grid-cols-3 gap-3">
              {[
                { k: "devices", v: "128", note: "protected" },
                { k: "threats", v: "3", note: "absorbed today" },
                { k: "avg trust", v: "81", note: "fleet score" },
              ].map((s) => (
                <div key={s.k} className="lg-chip p-4">
                  <div className="text-[9px] font-semibold uppercase tracking-[0.14em] opacity-70">{s.k}</div>
                  <div className="mt-1 font-mono text-3xl font-bold tabular-nums">{s.v}</div>
                  <div className="mt-1 text-[9px] font-semibold uppercase tracking-[0.14em]" style={{ color: "#8ef0b0" }}>{s.note}</div>
                </div>
              ))}
            </div>

            {/* Device rows */}
            <div className="mt-3 space-y-2">
              {ENDPOINTS.map((e, i) => (
                <motion.div
                  key={e.host}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="lg-chip flex items-center gap-4 px-4 py-3"
                >
                  <span
                    aria-hidden
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ background: STATE_COLOR[e.state], boxShadow: `0 0 6px ${STATE_COLOR[e.state]}` }}
                  />
                  <span className="w-44 shrink-0 truncate font-mono text-sm">{e.host}</span>
                  <span className="hidden w-40 shrink-0 text-[11px] font-medium opacity-70 sm:block">{e.os}</span>
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/15">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: STATE_COLOR[e.state] }}
                      initial={{ width: 0 }}
                      whileInView={{ width: `${e.trust}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.9, delay: 0.2 + i * 0.08, ease: "easeOut" }}
                    />
                  </div>
                  <span className="w-8 shrink-0 text-right font-mono text-sm tabular-nums">{e.trust}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
